import {
  Tabs,
  useBreadcrumbRoutes,
  Wrapper,
  Button,
  Table,
  usePaging,
  EllipsisSpan,
  Paging,
  useUpdate,
  Empty,
  Radio,
  Tooltip,
} from '@maxtropy/components';
import { Space } from 'antd';
import { apiV2MeterReadingCategoryListPost, apiV2MeterReadingDataQueryPost } from '@maxtropy/device-customer-apis-v2';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import DateSwitch from './DateSwitch';
import { RangeValue } from './DateSwitch';
import { TimeGranularity, TimeGranularityInitialValue } from './const';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import styles from './index.module.scss';
import ExportModal from './ExportModal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';
interface ScadaItem {
  id?: number;
  name?: string;
}

interface CategoryItem {
  id?: number;
  name?: string;
  areas?: ScadaItem[];
}

interface DataPropertyItem {
  id: number;
  name: string;
  unitName: string;
}

const isTimeGranularity = (value?: string | null): value is TimeGranularity =>
  Object.values(TimeGranularity).includes(value as TimeGranularity);

const getInitialTimeGranularity = (searchParams: URLSearchParams) => {
  const granularity = searchParams.get('granularity');
  return isTimeGranularity(granularity) ? granularity : TimeGranularity.HOUR;
};

const getInitialRangeValue = (searchParams: URLSearchParams, granularity: TimeGranularity): RangeValue => {
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const start = startDate ? dayjs(startDate) : null;
  const end = endDate ? dayjs(endDate) : null;
  if (start?.isValid() && end?.isValid()) {
    return [
      start.startOf(granularity === TimeGranularity.MONTH ? 'month' : 'date'),
      end.endOf(granularity === TimeGranularity.MONTH ? 'month' : 'date'),
    ];
  }
  return TimeGranularityInitialValue[granularity];
};

const StationMeterReading: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get('id');
  const tab = searchParams.get('tab');
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;
  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>(() =>
    getInitialTimeGranularity(searchParams)
  );
  const [value, setValue] = useState<RangeValue>(() =>
    getInitialRangeValue(searchParams, getInitialTimeGranularity(searchParams))
  );
  const [tabList, setTabList] = useState<{ key: string; label: string }[]>([]);
  const [categoryData, setCategoryData] = useState<CategoryItem[]>([]);
  const [activeTabKey, setActiveTabKey] = useState<string>('');
  const [selectedScadaId, setSelectedScadaId] = useState<number | undefined>();
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'export' | 'reRead'>('export');
  const [update, updateFn] = useUpdate();
  const hasB_MANUALMETERREADING = useHasPermission(PermissionsType.B_MANUALMETERREADING);

  const navigateWithQuery = useCallback(
    (nextId?: number, nextTab?: string, nextTimeGranularity?: TimeGranularity, nextValue?: RangeValue) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      if (nextId) {
        nextSearchParams.set('id', String(nextId));
      } else {
        nextSearchParams.delete('id');
      }
      if (nextTab) {
        nextSearchParams.set('tab', nextTab);
      } else {
        nextSearchParams.delete('tab');
      }
      if (nextTimeGranularity) {
        nextSearchParams.set('granularity', nextTimeGranularity);
      }
      if (nextValue !== undefined) {
        if (nextValue?.[0] && nextValue?.[1]) {
          nextSearchParams.set('startDate', nextValue[0].format('YYYY-MM-DD'));
          nextSearchParams.set('endDate', nextValue[1].format('YYYY-MM-DD'));
        } else {
          nextSearchParams.delete('startDate');
          nextSearchParams.delete('endDate');
        }
      }
      navigate(`/station/meter/reading?${nextSearchParams.toString()}`, { replace: true });
    },
    [navigate, searchParams]
  );

  const handleTimeGranularityChange = (nextTimeGranularity: TimeGranularity, nextValue: RangeValue) => {
    setTimeGranularity(nextTimeGranularity);
    setValue(nextValue);
    navigateWithQuery(selectedScadaId, activeTabKey, nextTimeGranularity, nextValue);
  };

  const handleRangeValueChange = (nextValue: RangeValue) => {
    setValue(nextValue);
    navigateWithQuery(selectedScadaId, activeTabKey, timeGranularity, nextValue);
  };

  // 打开导出弹窗
  const handleExport = () => {
    setModalMode('export');
    setExportModalVisible(true);
  };

  // 打开重新抄表弹窗
  const handleReReadMeter = () => {
    setModalMode('reRead');
    setExportModalVisible(true);
  };

  useEffect(() => {
    apiV2MeterReadingCategoryListPost({}).then(res => {
      const areas = res.list || [];
      setCategoryData(areas);
      // 设置 tab 列表
      const tabs = areas.map((item: CategoryItem) => ({
        key: String(item.id),
        label: item.name || `分类${item.id}`,
      }));
      setTabList(tabs);
      // 根据 URL 参数中的 stationId 自动选中站房
      if (id) {
        const parsedStationId = Number(id);
        // 遍历所有分类，找到包含该站房的分类并选中
        for (const category of areas) {
          const found = category.areas?.find(item => item.id === parsedStationId);
          if (found) {
            const nextTab = String(category.id);
            setActiveTabKey(nextTab);
            setSelectedScadaId(parsedStationId);
            if (tab !== nextTab) {
              navigateWithQuery(parsedStationId, nextTab);
            }
            break;
          }
        }
      } else if (tab) {
        const category = areas.find((item: CategoryItem) => String(item.id) === tab);
        if (category) {
          setActiveTabKey(tab);
          const defaultId = category.areas?.[0]?.id;
          if (defaultId) {
            setSelectedScadaId(defaultId);
            navigateWithQuery(defaultId, tab);
          }
        }
      } else if (areas.length > 0 && areas[0].areas && areas[0].areas.length > 0) {
        // 没有 id 参数时，跳转到第一个分类的第一个站房
        const defaultId = areas[0].areas[0].id;
        const defaultTab = String(areas[0].id);
        navigateWithQuery(defaultId, defaultTab);
      }
    });
  }, [id, navigateWithQuery, tab]);

  // 获取当前选中分类的站房列表
  const currentCategory = categoryData.find(item => String(item.id) === activeTabKey);
  const scadaList = currentCategory?.areas || [];

  // 获取抄表数据列表
  const { data: meterReadingData, loading: tableLoading } = useRequest(
    () => {
      const startDate = value?.[0]?.format('YYYY-MM-DD');
      const endDate = value?.[1]?.format('YYYY-MM-DD');
      return apiV2MeterReadingDataQueryPost({
        areaId: selectedScadaId,
        granularityType:
          timeGranularity === TimeGranularity.MONTH
            ? 'MONTH'
            : timeGranularity === TimeGranularity.DAY
            ? 'DAY'
            : 'HOUR',
        startDate,
        endDate,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res?.total ?? 0);
        return res.list || [];
      });
    },
    {
      refreshDeps: [pageOffset, pageSize, selectedScadaId, value, timeGranularity, update],
      ready: !!selectedScadaId,
    }
  );

  // 将扁平数据转换为按抄表时间分行的数据，每个设备的数据属性作为列
  const { tableDataSource, dataPropertiesMap, deviceList } = useMemo(() => {
    const readingTimeMap = new Map<string, any>();
    // 按设备分别收集数据属性
    const deviceDataPropertyMap = new Map<number, Map<number, DataPropertyItem>>();
    const deviceMap = new Map<number, { deviceId: number; deviceName: string }>();

    // 根据时间粒度确定日期格式（保持UTC时间直接格式化）
    const getTimeKey = (readingTime: string) => {
      const date = dayjs(readingTime);
      switch (timeGranularity) {
        case TimeGranularity.MONTH:
          return date.format('YYYY-MM');
        case TimeGranularity.DAY:
          return date.format('YYYY-MM-DD');
        case TimeGranularity.HOUR:
        default:
          return date.format('YYYY-MM-DD HH:00');
      }
    };

    // 首先收集各设备的属性
    meterReadingData?.forEach(item => {
      // 收集设备
      if (item.deviceId && item.deviceName) {
        deviceMap.set(item.deviceId, {
          deviceId: item.deviceId,
          deviceName: item.deviceName,
        });
        // 初始化设备属性Map
        if (!deviceDataPropertyMap.has(item.deviceId)) {
          deviceDataPropertyMap.set(item.deviceId, new Map());
        }
      }

      // 收集该设备的属性
      if (item.dataPropertyId && item.deviceId) {
        const propMap = deviceDataPropertyMap.get(item.deviceId);
        if (propMap) {
          propMap.set(item.dataPropertyId, {
            id: item.dataPropertyId,
            name: item.dataPropertyName || '',
            unitName: item.physicalUnitName || '',
          });
        }
      }

      // 填入数据值
      if (item.readingTime && item.deviceId) {
        const timeKey = getTimeKey(item.readingTime);
        const existingData = readingTimeMap.get(timeKey);
        if (existingData) {
          // 已存在该抄表时间，添加或更新该设备的数据属性值
          const key = `device_${item.deviceId}_value_${item.dataPropertyId}`;
          existingData[key] = item.value?.toFixed(2);
        } else {
          // 新抄表时间数据
          const readingData: any = {
            readingTime: timeKey,
          };
          const key = `device_${item.deviceId}_value_${item.dataPropertyId}`;
          readingData[key] = item.value?.toFixed(2);
          readingTimeMap.set(timeKey, readingData);
        }
      }
    });

    // 转换为设备属性Map
    const dataPropertiesMapResult = new Map<number, DataPropertyItem[]>();
    deviceDataPropertyMap.forEach((props, deviceId) => {
      dataPropertiesMapResult.set(deviceId, Array.from(props.values()));
    });

    // 按抄表时间正序排序
    const tableData = Array.from(readingTimeMap.values()).sort((a, b) => {
      return dayjs(a.readingTime).valueOf() - dayjs(b.readingTime).valueOf();
    });

    return {
      tableDataSource: tableData,
      dataPropertiesMap: dataPropertiesMapResult,
      deviceList: Array.from(deviceMap.values()),
    };
  }, [meterReadingData, timeGranularity]);

  // 构建表格列 - 两行表头结构
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: '',
        children: [
          {
            title: '抄表时间',
            dataIndex: 'readingTime',
            key: 'readingTime',
            fixed: 'left',
            width: 150,
            ellipsis: { showTitle: true },
            render: (v: string) => <EllipsisSpan value={v} />,
          },
        ],
      },
    ];

    // 按设备分列，每个设备一个列组
    deviceList.forEach(device => {
      const props = dataPropertiesMap.get(device.deviceId) || [];
      const deviceColumns = props.map((prop: DataPropertyItem) => ({
        title: `${prop.name} (${prop.unitName})`,
        dataIndex: `device_${device.deviceId}_value_${prop.id}`,
        key: `device_${device.deviceId}_value_${prop.id}`,
        width: 200,
        align: 'left' as const,
        ellipsis: false,
        render: (v: number | null) => (v === null || v === undefined ? '-' : v),
      }));

      cols.push({
        title: device.deviceName,
        children: deviceColumns,
        ellipsis: false,
        align: 'left' as const,
      });
    });

    return cols;
  }, [deviceList, dataPropertiesMap]);

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className={styles.content}>
      <Tabs
        items={tabList}
        activeKey={activeTabKey}
        onChange={key => {
          setActiveTabKey(key);
          // 切换 Tab 时，默认选中该分类下的第一个站房
          const category = categoryData.find(item => String(item.id) === key);
          if (category?.areas && category.areas.length > 0) {
            const firstScada = category.areas[0];
            setSelectedScadaId(firstScada.id);
            navigateWithQuery(firstScada.id, key, timeGranularity, value);
          } else {
            setSelectedScadaId(undefined);
            navigateWithQuery(undefined, key, timeGranularity, value);
          }
        }}
        className={styles.tabs}
      />
      {/* 站房单选框 */}
      {scadaList.length > 0 && (
        <div className={styles.scadaList}>
          <Radio.Group
            value={selectedScadaId}
            onChange={e => {
              setSelectedScadaId(e.target.value);
              navigateWithQuery(e.target.value, activeTabKey, timeGranularity, value);
            }}
            optionType="button"
            buttonStyle="solid"
          >
            {scadaList.map((scada: ScadaItem) => (
              <Radio.Button key={scada.id} value={scada.id}>
                <Tooltip title={scada.name && scada.name.length > 12 ? scada.name : undefined}>
                  {scada.name && scada.name.length > 12 ? `${scada.name.slice(0, 12)}...` : scada.name}
                </Tooltip>
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      )}
      <div className={styles.toolbar}>
        <DateSwitch
          timeGranularity={timeGranularity}
          onTimeGranularityChange={handleTimeGranularityChange}
          value={value}
          onValueChange={handleRangeValueChange}
        />
        <Space size="small">
          {hasB_MANUALMETERREADING && (
            <Button type="primary" onClick={handleReReadMeter}>
              手动重新抄表
            </Button>
          )}
          <Button type="primary" onClick={handleExport}>
            导出
          </Button>
        </Space>
      </div>
      {tableDataSource.length > 0 ? (
        <Table
          dataSource={tableDataSource}
          columns={columns}
          rowKey="readingTime"
          loading={tableLoading}
          scroll={{ x: 'max-content' }}
          pagination={false}
          bordered
        />
      ) : (
        <Empty description="暂无数据" />
      )}
      <Paging pagingInfo={pagingInfo} />
      <ExportModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        modalMode={modalMode}
        updateFn={updateFn}
      />
    </Wrapper>
  );
};

export default StationMeterReading;
